/**
 * Mock Blast Radius Data Utilities
 * Provides comprehensive mock blast radius data for GitHub and S3 integrations
 */

import { GITHUB_REPOS, S3_BUCKETS } from './mockData';

// Common GitHub scopes
const GITHUB_SCOPES = [
  { scope: 'repo', permissions: ['read', 'write'], repositories_count: 25 },
  { scope: 'read:org', permissions: ['read'], organizations_count: 5 },
  { scope: 'admin:org', permissions: ['admin'], organizations_count: 2 },
  { scope: 'workflow', permissions: ['write'], repositories_count: 15 },
  { scope: 'read:user', permissions: ['read'], users_count: 50 },
  { scope: 'user:email', permissions: ['read'], emails_count: 10 },
  { scope: 'gist', permissions: ['write'], gists_count: 5 },
  { scope: 'notifications', permissions: ['read'], notifications_count: 100 },
];

// Common AWS services
const AWS_SERVICES = [
  { service: 'S3', resource_count: 15, permission_level: 'read-write' },
  { service: 'EC2', resource_count: 8, permission_level: 'read-only' },
  { service: 'Lambda', resource_count: 12, permission_level: 'read-write' },
  { service: 'RDS', resource_count: 5, permission_level: 'read-only' },
  { service: 'IAM', resource_count: 10, permission_level: 'read-write' },
  { service: 'CloudFormation', resource_count: 7, permission_level: 'read' },
  { service: 'SQS', resource_count: 9, permission_level: 'read-write' },
  { service: 'SNS', resource_count: 6, permission_level: 'read' },
  { service: 'DynamoDB', resource_count: 4, permission_level: 'read-write' },
  { service: 'CloudWatch', resource_count: 11, permission_level: 'read' },
];

// Common organizations for GitHub
const ORGS = [
  'acme-corp',
  'tech-startup',
  'enterprise-inc',
  'open-source-foundation',
  'dev-team-org',
];

/**
 * Generate comprehensive GitHub blast radius data
 * @param {string} insightId - Insight/finding ID
 * @param {string} owner - Repository owner (optional)
 * @param {string} repoName - Repository name (optional)
 * @returns {Object} Comprehensive blast radius data compatible with normalizeInsight()
 */
export const generateGitHubBlastRadius = (insightId, owner = 'acme-corp', repoName = 'main-repo') => {
  // Generate 20-30 repositories with mixed visibility
  const repoCount = 25;
  const repositories = [];
  const usedRepos = new Set();
  
  for (let i = 0; i < repoCount; i++) {
    const repo = GITHUB_REPOS[i % GITHUB_REPOS.length];
    const fullName = `${repo.owner}/${repo.name}`;
    
    // Avoid duplicates
    if (!usedRepos.has(fullName)) {
      usedRepos.add(fullName);
      repositories.push({
        name: repo.name,
        owner: repo.owner,
        full_name: fullName,
        url: `https://github.com/${fullName}`,
        visibility: Math.random() > 0.4 ? 'public' : 'private',
      });
    } else {
      // Use variations
      repositories.push({
        name: `${repo.name}-${i}`,
        owner: repo.owner,
        full_name: `${repo.owner}/${repo.name}-${i}`,
        url: `https://github.com/${repo.owner}/${repo.name}-${i}`,
        visibility: Math.random() > 0.4 ? 'public' : 'private',
      });
    }
  }

  // Generate organizations
  const organizations = ORGS.map((orgName, idx) => ({
    name: orgName,
    id: `org-${idx + 1}`,
    url: `https://github.com/${orgName}`,
  }));

  // Select random scopes (5-8 scopes)
  const selectedScopesCount = Math.floor(Math.random() * 4) + 5;
  const selectedScopes = GITHUB_SCOPES.slice(0, selectedScopesCount);

  // Generate token info
  const tokenCreatedAt = new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString();
  const hasExpiration = Math.random() > 0.5;
  const tokenExpiration = hasExpiration 
    ? new Date(Date.now() + Math.random() * 180 * 24 * 60 * 60 * 1000).toISOString()
    : null;
  const daysRemaining = hasExpiration 
    ? Math.floor((new Date(tokenExpiration) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return {
    provider: 'GitHub',
    user: owner || 'acme-corp',
    token_user: owner || 'acme-corp',
    token_type: 'GitHub Personal Access Token',
    token_created_at: tokenCreatedAt,
    token_expiration: tokenExpiration,
    days_remaining: daysRemaining,
    token_info: {
      token: `ghp_${Math.random().toString(36).substr(2, 30)}`,
      created_at: tokenCreatedAt,
      scopes: selectedScopes.map(s => s.scope),
    },
    repositories: repositories,
    total_repositories: repositories.length,
    scopes: selectedScopes.map(s => ({
      scope: s.scope,
      in_scope: true,
      permissions: s.permissions,
      repositories_count: s.repositories_count || 0,
      organizations_count: s.organizations_count || 0,
    })),
    total_scopes: selectedScopes.length,
    organizations: organizations,
    total_organizations: organizations.length,
  };
};

/**
 * Generate comprehensive S3 blast radius data
 * @param {string} findingId - Finding ID
 * @param {string} bucketName - Bucket name (optional)
 * @returns {Object} Comprehensive blast radius data compatible with normalizeInsight()
 */
export const generateS3BlastRadius = (findingId, bucketName = 'prod-logs-backup') => {
  // Generate 15-25 affected buckets
  const bucketCount = 20;
  const affectedBuckets = [];
  const usedBuckets = new Set();
  
  for (let i = 0; i < bucketCount; i++) {
    const bucket = S3_BUCKETS[i % S3_BUCKETS.length];
    
    // Avoid duplicates
    if (!usedBuckets.has(bucket.name)) {
      usedBuckets.add(bucket.name);
      const permissionLevels = ['read-only', 'read-write', 'full-access'];
      affectedBuckets.push({
        name: bucket.name,
        region: bucket.region,
        permission_level: permissionLevels[Math.floor(Math.random() * permissionLevels.length)],
        url: `s3://${bucket.name}`,
      });
    } else {
      // Use variations
      const newName = `${bucket.name}-${i}`;
      const permissionLevels = ['read-only', 'read-write', 'full-access'];
      affectedBuckets.push({
        name: newName,
        region: bucket.region,
        permission_level: permissionLevels[Math.floor(Math.random() * permissionLevels.length)],
        url: `s3://${newName}`,
      });
    }
  }

  // Generate IAM permissions
  const iamPermissions = [
    { permission: 's3:GetObject', resources: ['*'], effect: 'Allow' },
    { permission: 's3:PutObject', resources: ['*'], effect: 'Allow' },
    { permission: 's3:ListBucket', resources: ['*'], effect: 'Allow' },
    { permission: 's3:DeleteObject', resources: ['*'], effect: 'Allow' },
    { permission: 'ec2:DescribeInstances', resources: ['*'], effect: 'Allow' },
    { permission: 'ec2:DescribeImages', resources: ['*'], effect: 'Allow' },
    { permission: 'lambda:InvokeFunction', resources: ['arn:aws:lambda:*:*:function:*'], effect: 'Allow' },
    { permission: 'rds:DescribeDBInstances', resources: ['*'], effect: 'Allow' },
    { permission: 'iam:GetUser', resources: ['*'], effect: 'Allow' },
    { permission: 'iam:ListRoles', resources: ['*'], effect: 'Allow' },
  ];

  // Select AWS services (7-10 services)
  const selectedServicesCount = Math.floor(Math.random() * 4) + 7;
  const selectedServices = AWS_SERVICES.slice(0, selectedServicesCount);

  // Generate access key info
  const keyCreatedAt = new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString();
  const lastUsed = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString();

  return {
    provider: 'AWS',
    user: 'aws-user',
    token_user: 'aws-user',
    token_type: 'AWS Access Key',
    access_key_info: {
      access_key_id: `AKIA${Math.random().toString(36).substr(2, 16).toUpperCase()}`,
      created_at: keyCreatedAt,
      last_used: lastUsed,
      permissions: iamPermissions.map(p => p.permission),
    },
    token_created_at: keyCreatedAt,
    affected_buckets: affectedBuckets,
    total_buckets: affectedBuckets.length,
    iam_permissions: iamPermissions,
    total_permissions: iamPermissions.length,
    aws_services: selectedServices,
    total_services: selectedServices.length,
    // Convert to scopes format for compatibility with normalization
    scopes: selectedServices.map((service, idx) => ({
      scope: service.service.toLowerCase(),
      in_scope: true,
      permission: service.permission_level,
      resource_count: service.resource_count,
    })),
    total_scopes: selectedServices.length,
  };
};
